import { compose, filter, flatten, groupBy, isEmpty, isUndefined, map } from 'lodash/fp';
import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array, number, ord, record, semigroup } from 'fp-ts';
import { ResourceDataDiscriminator, Resource, ResourceStateUpdate } from '@mews-commander/resources';
import { ResourceCategoryType, GeneralResourceCategoryTier as ResourceCategoryTier } from '@mews-commander/data-access';
import { getNumberProp, getStringProp, keyById } from '@mews/fp';
import { createAction, createAsyncActionTypes } from '@mews/framework';
import { createHandler } from '@mews/std';
import { CommanderThunkAction } from '@mews-commander/types';
import { createBackgroundAction, createScopedReducer } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { fetchResources } from '../actions/resources';
import { EMPTY_CATEGORY, EMPTY_RESOURCE_CATEGORY_ASSIGNMENT, MAX_SPACE_AGE, RESOURCE_EXTENT } from '../constants';
import prefix from '../prefix';
import moduleSelector from '../selector';
import { constructResourcesTree } from './resourcesTree';

export const NAME = 'spaces';

const SPACES_FETCH = prefix('SPACES_FETCH');
export const [SPACES_FETCH_STARTED, SPACES_FETCH_SUCCEEDED, SPACES_FETCH_FAILED] = createAsyncActionTypes(SPACES_FETCH);

export const SPACES_MODIFIED = prefix('SPACES_MODIFIED');

export function fetchSpaces(enterpriseId: $TSFixMe, extent = RESOURCE_EXTENT) {
    return (dispatch: $TSFixMe, getState: $TSFixMe, { appClockService }: $TSFixMe) => {
        // @ts-expect-error $TSFixMeError: Binding element 'data' implicitly has an 'any' type.
        const promise = dispatch(fetchResources({ extent })).then(({ value: data }) =>
            transformSpacesPayload(data, getState, { appClockService })
        );

        return dispatch(createBackgroundAction(SPACES_FETCH, { promise }, { scope: enterpriseId }));
    };
}

export function transformSpacesPayload(
    { resourceCategories, slots, resources, resourceCategoryAssignments }: $TSFixMe,
    getState: $TSFixMe,
    { appClockService }: $TSFixMe
) {
    const resourceCategoriesMap = keyById(resourceCategories);
    const resourceCategoryAssignmentsMap = groupBy('resourceId', resourceCategoryAssignments);
    const mappedResources = compose(
        flatten,
        map((resource) => transformSpace(resource, resourceCategoriesMap, resourceCategoryAssignmentsMap))
    )([...resources, ...slots]);
    return {
        spaces: mappedResources,
        spaceCategories: resourceCategories,
        lastUpdated: appClockService.getValue(),
    };
}

export const modifySpaceStates =
    (updates: Array<ResourceStateUpdate>): CommanderThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();

        const currentResources = getSpaces(state);

        const newResources = currentResources?.map((resource: $TSFixMe) => {
            const foundUpdate = updates.find((update) => update.resourceId === resource.id);
            if (!foundUpdate) return resource;

            const newResource = {
                ...resource,
                state: foundUpdate.state ?? resource.state,
                stateReason: foundUpdate.stateReason ?? resource.stateReason,
            };
            return newResource;
        });

        return dispatch(createAction(SPACES_MODIFIED, newResources, { scope: enterpriseId }));
    };

function transformSpace(resource: $TSFixMe, resourceCategoriesMap: $TSFixMe, resourceCategoryAssignmentsMap: $TSFixMe) {
    const foundCategoryAssignments = resourceCategoryAssignmentsMap[resource.id];
    const categoryAssignments = isEmpty(foundCategoryAssignments)
        ? [EMPTY_RESOURCE_CATEGORY_ASSIGNMENT]
        : foundCategoryAssignments;
    return map(({ categoryId: assignmentCategoryId }) => {
        const categoryId = resource?.categoryId || assignmentCategoryId;
        const category = resourceCategoriesMap[categoryId] || EMPTY_CATEGORY;
        return {
            data: { discriminator: ResourceDataDiscriminator.Space },
            ...resource,
            normalBedCount: category?.capacity || 0,
            extraBedCount: category?.extraCapacity || 0,
            resourceCategoryId: categoryId,
            resourceCategoryName: category?.name,
            resourceCategoryShortName: category?.shortName,
            resourceCategoryOrdering: category?.ordering,
            resourceCategoryType: category?.type,
            isSlot: isUndefined(resource.name),
            floorNumber: resource?.data?.value?.floorNumber,
            number: resource?.name,
        };
    })(categoryAssignments);
}

const INITIAL_STATE = {
    fetching: false,
    lastUpdated: 0,
    spaces: [],
    spaceCategories: [],
};

export const spacesReducer = createScopedReducer(INITIAL_STATE, {
    [SPACES_FETCH_STARTED]: (state: $TSFixMe) => ({
        ...state,
        fetching: true,
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [SPACES_FETCH_SUCCEEDED]: (state, payload) => ({
        ...state,
        ...payload,
        fetching: false,
    }),
    [SPACES_FETCH_FAILED]: (state: $TSFixMe) => ({
        ...state,
        fetching: false,
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [SPACES_MODIFIED]: (state, payload) => ({
        ...state,
        spaces: payload,
    }),
});
const spacesModuleSelector = createSelector(moduleSelector, (state) => state[NAME]);
export const getSpacesState = createSelector(
    spacesModuleSelector,
    getCurrentEnterpriseId,
    (state, scope) => state?.[scope] ?? INITIAL_STATE
);
export const getEnterpriseSpacesState = createSelector(
    spacesModuleSelector,
    getStringProp('enterpriseId'),
    (state, scope) => state?.[scope] ?? INITIAL_STATE
);
export const getSpaces = createSelector(getSpacesState, (state) => state.spaces);
export const getEnterpriseSpaces = createSelector(getEnterpriseSpacesState, (state) => state.spaces);

export const getSortedSpaces = createSelector(getSpaces, constructResourcesTree);
export const getSpaceCategories = createSelector(getSpacesState, (state) => state.spaceCategories);

const constructResourceIdToOrderingMap = flow(
    array.mapWithIndex((sortedIndex, resource: Resource): [string, number] => [resource.id, sortedIndex]),
    record.fromFoldable(semigroup.first<number>(), array.Foldable)
);
export const getResourceIdOrd = createSelector(getSortedSpaces, ({ resources }) => {
    const resourceIdToOrderMap = constructResourceIdToOrderingMap(resources);

    const getOrdering = (resourceId: string) => resourceIdToOrderMap[resourceId] ?? -1;
    return ord.contramap(getOrdering)(number.Ord);
});
export const getResourceCategoryIdOrd = createSelector(getSortedSpaces, ({ resources }) => {
    const resourceCategoryIdToOrderMap = pipe(
        resources,
        array.mapWithIndex((sortedIndex, resource): [string, number] => [resource.resourceCategoryId, sortedIndex]),
        record.fromFoldable(semigroup.first<number>(), array.Foldable)
    );

    const getOrdering = (resourceCategoryId: string) => resourceCategoryIdToOrderMap[resourceCategoryId] ?? -1;
    return ord.contramap(getOrdering)(number.Ord);
});
export const getServiceCategories = createSelector(
    getStringProp('serviceId'),
    getSpaceCategories,
    (serviceId, categories) => filter({ serviceId })(categories)
);

export const isFetching = createSelector(getSpacesState, (state) => state.fetching);
export const spacesNeedsUpdate = createSelector(
    getSpacesState,
    getNumberProp('appClockValue'),
    (state, appClockValue) => appClockValue - state.lastUpdated > MAX_SPACE_AGE
);

export const mapResourceCategoryTypeToTier = createHandler(
    {
        [ResourceCategoryType.Apartment]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.Bed]: ResourceCategoryTier.Small,
        [ResourceCategoryType.Desk]: ResourceCategoryTier.Small,
        [ResourceCategoryType.Dorm]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.MeetingRoom]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.Office]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.ParkingSpot]: ResourceCategoryTier.Small,
        [ResourceCategoryType.Room]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.Site]: ResourceCategoryTier.Large,
        [ResourceCategoryType.Suite]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.TeamArea]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.Villa]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.Tent]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.CaravanOrRV]: ResourceCategoryTier.Medium,
        [ResourceCategoryType.UnequippedCampsite]: ResourceCategoryTier.Small,
        [ResourceCategoryType.Membership]: ResourceCategoryTier.Small,
        [ResourceCategoryType.Bike]: ResourceCategoryTier.Small,
        [ResourceCategoryType.ExtraBed]: ResourceCategoryTier.Small,
        [ResourceCategoryType.Cot]: ResourceCategoryTier.Small,
        [ResourceCategoryType.Crib]: ResourceCategoryTier.Small,
        [ResourceCategoryType.ConferenceRoom]: ResourceCategoryTier.Large,
        [ResourceCategoryType.Rooftop]: ResourceCategoryTier.Large,
        [ResourceCategoryType.Garden]: ResourceCategoryTier.Large,
        [ResourceCategoryType.Restaurant]: ResourceCategoryTier.Large,
        [ResourceCategoryType.Amphitheater]: ResourceCategoryTier.Large,
        [ResourceCategoryType.PrivateSpaces]: ResourceCategoryTier.Large,
    },
    // @ts-expect-error $TSFixMeError: Argument of type '() => undefined' is not assignable to parameter of type 'ResourceCategoryTier'.
    () => undefined
);
